
import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Upload, Plus, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authenticatedFetch } from '../context/apiClient';

const LLMHallucinationTester = () => {
  const { user, logout } = useAuth();
  const pollingRef = useRef(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [isTestingStarted, setIsTestingStarted] = useState(false);
  const [apiResults, setApiResults] = useState(null);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [useSystemQuestions, setUseSystemQuestions] = useState(false);
  const [systemQuestionCount, setSystemQuestionCount] = useState(10);
  const [uploadedQuestions, setUploadedQuestions] = useState([]);
  const [manualQuestions, setManualQuestions] = useState([]);
  const [models, setModels] = useState([]);

  useEffect(() => {
    const fetchModels = async () => {
      if (!user?.token) return;
      try {
        const response = await authenticatedFetch('/api/config', {}, user.token, logout);
        if (response.ok) {
          const data = await response.json();
          if (data.models) {
            setModels(data.models.map(m => m.name));
          }
        }
      } catch (error) {
        console.error('Error fetching models:', error);
      }
    };
    fetchModels();
  }, [user, logout]);

  const methods = [
    {"label":"semantic entropy","value":"semantic_entropy"},
    {"label":"sac3","value":"sac3"}
  ];

  const systemQuestions = Array.from({ length: 800 }, (_, i) => ({
    id: `sys-${i + 1}`,
    category: 'System',
    question: `System generated hallucination test question ${i + 1}`,
    correctAnswer: '',
    commonHallucination: ''
  }));

  const finalQuestions = [    
    ...uploadedQuestions,
    ...manualQuestions
  ];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const parsed = JSON.parse(content);
          if (Array.isArray(parsed)) {
            const formattedQuestions = parsed.map((q, index) => ({
              id: `upload-${Date.now()}-${index}`,
              category: q.category || 'Uploaded',
              question: q.question || q.text || '',
              correctAnswer: q.correctAnswer || q.answer || '',
              commonHallucination: q.commonHallucination || q.hallucination || ''
            }));
            setUploadedQuestions([...uploadedQuestions, ...formattedQuestions]);
            alert(`Successfully imported ${formattedQuestions.length} questions!`);
          }
        } catch (error) {
          const content = e.target.result;
          const lines = content.split('\n').filter(line => line.trim());
          const formattedQuestions = lines.map((line, index) => {
            const parts = line.split(',').map(p => p.trim());
            return {
              id: `upload-${Date.now()}-${index}`,
              category: parts[0] || 'Uploaded',
              question: parts[1] || line,
              correctAnswer: parts[2] || '',
              commonHallucination: parts[3] || ''
            };
          });
          setUploadedQuestions([...uploadedQuestions, ...formattedQuestions]);
          alert(`Successfully imported ${formattedQuestions.length} questions!`);
        }
      };
      reader.readAsText(file);
    }
  };

  const addQuestion = () => {
    if (newQuestion.trim()) {
      const question = {
        id: `manual-${Date.now()}`,
        category: 'Manual',
        question: newQuestion,
        correctAnswer: '',
        commonHallucination: ''
      };
      setManualQuestions([...manualQuestions, question]);
      setNewQuestion('');
      setShowAddQuestion(false);
    }
  };

  const deleteUploadedQuestion = (id) => {
    setUploadedQuestions(uploadedQuestions.filter(q => q.id !== id));
  };

  const deleteManualQuestion = (id) => {
    setManualQuestions(manualQuestions.filter(q => q.id !== id));
  };

  const canStartTest = selectedModel && selectedMethod && finalQuestions.length > 0;

  const exportToCSV = () => {
    if (!apiResults || !apiResults.data) {
      alert('No results to export');
      return;
    }

    const headers = ['#', 'Question', 'Model Response', 'Result'];
    const rows = apiResults.data.map((result, index) => [
      index + 1,
      `"${result.question.replace(/"/g, '""')}"`,
      `"${result.pred_answer.replace(/"/g, '""')}"`,
      result.is_hallucination === 0 ? 'Passed' :
        result.is_hallucination === 1 ? 'Failed' :
        'Uncertain'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `hallucination_test_results_${selectedModel}_${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const startTest = () => {
    setIsTestingStarted(true);
    pollingRef.current = true;
    fetchApiResults();
  };

  const handleStopTest = () => {
    pollingRef.current = false;
    setIsLoadingResults(false);
    setIsTestingStarted(false);
  };

  const resetTest = () => {
    pollingRef.current = false;
    setIsTestingStarted(false);
    setApiResults(null);
    setIsLoadingResults(false);
    setUploadedQuestions([]);
    setManualQuestions([]);
    setUseSystemQuestions(false);
    setSystemQuestionCount(10);
    setSelectedModel('');
    setSelectedMethod('');
  };

  const pollResults = async (scanId) => {
    if (!pollingRef.current) return;
    try {
      const response = await authenticatedFetch(`/api/hal_results/${scanId}`, {}, user?.token, logout);
      if (!pollingRef.current) return;
      if (response.ok) {
        const data = await response.json();
        if (data.data && data.data.length > 0) {
          setApiResults(data);
          setIsLoadingResults(false);
          pollingRef.current = false;
        } else {
          if (pollingRef.current) setTimeout(() => pollResults(scanId), 2000);
        }
      } else {
        if (pollingRef.current) setTimeout(() => pollResults(scanId), 2000);
      }
    } catch (error) {
      console.error('Polling error:', error);
      // if (pollingRef.current) setTimeout(() => pollResults(scanId), 2000);
    }
  };

  const fetchApiResults = async () => {
    setIsLoadingResults(true);
    try {
      const payload = {       
        scan_type: "hallucination_detect",
        model_name: selectedModel,
        method: selectedMethod,
        size: useSystemQuestions ? systemQuestionCount : 0,
        questions: finalQuestions.map(q => ({ question: q.question }))
      };
      
      const response = await authenticatedFetch('/api/hallucination', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }, user?.token, logout);

      if (!response.ok) throw new Error('Failed to fetch results');
      
      const data = await response.json();
      if (data.scan_id) {
        if (pollingRef.current) pollResults(data.scan_id);
      } else {
        throw new Error('No scan_id returned');
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      alert('Error fetching results from API');
      setIsLoadingResults(false);
      pollingRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">LLM Hallucination Tester</h1>
          <p className="text-gray-600">Test and evaluate language models for hallucination patterns</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-medium mb-3">Select Model</label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choose a model...</option>
                {models.map(model => (
                  <option key={model} value={model}>{model}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-3">Select Method</label>
              <select
                value={selectedMethod}
                onChange={(e) => setSelectedMethod(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choose a method...</option>
                {methods.map(method => (
                  <option key={method.value} value={method.value}>{method.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Manage Questions</h2>
          
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <label className="flex items-center gap-2 text-gray-700 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={useSystemQuestions}
                onChange={(e) => setUseSystemQuestions(e.target.checked)}
                className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="font-medium">Use System Questions</span>
            </label>
            {useSystemQuestions && (
              <div className="ml-6">
                <label className="block text-sm text-gray-600 mb-2">
                  Number of system questions (max 800):
                </label>
                <input
                  type="number"
                  min="1"
                  max="800"
                  value={systemQuestionCount}
                  onChange={(e) => setSystemQuestionCount(Math.min(800, Math.max(1, parseInt(e.target.value) || 1)))}
                  className="w-32 border border-gray-300 rounded px-3 py-1"
                />
                <span className="ml-2 text-sm text-gray-500">questions selected</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <label className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-3 rounded cursor-pointer">
              <Upload size={20} />
              Upload from CSV
              <input
                type="file"
                accept=".json,.csv,.txt"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
            <button
              onClick={() => setShowAddQuestion(!showAddQuestion)}
              className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded"
            >
              <Plus size={20} />
              Add Question Manually
            </button>
          </div>

          {showAddQuestion && (
            <div className="border border-gray-300 rounded-lg p-4 mb-4 bg-gray-50">
              <h3 className="font-medium text-gray-900 mb-3">Add New Question</h3>
              <div className="space-y-3">
                <textarea
                  placeholder="Enter your question"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 h-24"
                />
                <div className="flex gap-2">
                  <button
                    onClick={addQuestion}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
                  >
                    Save Question
                  </button>
                  <button
                    onClick={() => setShowAddQuestion(false)}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="text-sm text-gray-600 mb-4">
            <div className="space-y-1">
              <div><strong>Total Questions:</strong> <span className="font-bold text-lg">{finalQuestions.length}</span></div>
              {useSystemQuestions && <div className="text-blue-600">• System Questions: {systemQuestionCount}</div>}
              {uploadedQuestions.length > 0 && <div className="text-purple-600">• Uploaded Questions: {uploadedQuestions.length}</div>}
              {manualQuestions.length > 0 && <div className="text-green-600">• Manual Questions: {manualQuestions.length}</div>}
            </div>
          </div>

          {uploadedQuestions.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium text-gray-900">Uploaded Questions ({uploadedQuestions.length})</h3>
                <button
                  onClick={() => setUploadedQuestions([])}
                  className="text-sm text-red-500 hover:text-red-700"
                >
                  Clear All
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {uploadedQuestions.map((q, index) => (
                  <div key={q.id} className="flex justify-between items-start bg-white p-3 rounded border border-purple-200">
                    <div className="flex-1">
                      <span className="text-gray-600 text-sm mr-2">{index + 1}.</span>
                      <span className="text-gray-900">{q.question}</span>
                    </div>
                    <button
                      onClick={() => deleteUploadedQuestion(q.id)}
                      className="text-red-500 hover:text-red-700 ml-2"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {manualQuestions.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium text-gray-900">Manual Questions ({manualQuestions.length})</h3>
                <button
                  onClick={() => setManualQuestions([])}
                  className="text-sm text-red-500 hover:text-red-700"
                >
                  Clear All
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {manualQuestions.map((q, index) => (
                  <div key={q.id} className="flex justify-between items-start bg-white p-3 rounded border border-green-200">
                    <div className="flex-1">
                      <span className="text-gray-600 text-sm mr-2">{index + 1}.</span>
                      <span className="text-gray-900">{q.question}</span>
                    </div>
                    <button
                      onClick={() => deleteManualQuestion(q.id)}
                      className="text-red-500 hover:text-red-700 ml-2"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {!isTestingStarted && finalQuestions.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm text-center">
            <button
              onClick={startTest}
              disabled={!canStartTest || isLoadingResults}
              className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg text-lg font-semibold"
            >
              {isLoadingResults ? 'Running Test...' : 'Start Test'}
            </button>
            {!canStartTest && (
              <p className="text-gray-500 text-sm mt-2">
                Please select both model and method to start
              </p>
            )}
          </div>
        )}

        {isTestingStarted && isLoadingResults && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm text-center">
            
            <div className="mt-2 text-gray-500">Please wait while we process your questions</div>
            {/* <button
              onClick={handleStopTest}
              className="mt-4 bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Stop Test
            </button> */}
          </div>
        )}

        {isTestingStarted && apiResults && (
          <>
            {apiResults.summary && (
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="text-gray-600 text-sm mb-1">Total</div>
                  <div className="text-2xl font-bold text-gray-900">{apiResults.summary.total}</div>
                </div>
                <div className="bg-white border border-green-200 rounded-lg p-4 shadow-sm">
                  <div className="text-gray-600 text-sm mb-1">Passed</div>
                  <div className="text-2xl font-bold text-green-600">{apiResults.summary.passed}</div>
                </div>
                <div className="bg-white border border-red-200 rounded-lg p-4 shadow-sm">
                  <div className="text-gray-600 text-sm mb-1">Failed</div>
                  <div className="text-2xl font-bold text-red-600">{apiResults.summary.failed}</div>
                </div>
                <div className="bg-white border border-yellow-200 rounded-lg p-4 shadow-sm">
                  <div className="text-gray-600 text-sm mb-1">Uncertain</div>
                  <div className="text-2xl font-bold text-yellow-600">{apiResults.summary.uncertain}</div>
                </div>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm overflow-x-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Test Results</h2>
                <div className="flex gap-2">
                  <button
                    onClick={resetTest}
                    className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                  >
                    <X size={16} />
                    New Test
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                  >
                    <Upload size={16} />
                    Export to CSV
                  </button>
                </div>
              </div>
              
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-300">
                    <th className="text-left p-3 font-semibold text-gray-700">#</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Question</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Model Response</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Result</th>
                    {/* <th className="text-center p-3 font-semibold text-gray-700">Confidence</th> */}
                    
                  </tr>
                </thead>
                <tbody>
                  {apiResults.data.map((result, index) => (
                    <tr key={result.questionId} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="p-3 text-gray-600">{index + 1}</td>
                      <td className="p-3 text-gray-900 max-w-xs">
                        {result.question}
                      </td>
                      <td className="p-3 text-gray-700 max-w-md">
                        <div title={result.pred_answer}>
                          {result.pred_answer}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          result.is_hallucination === 0 ? 'bg-green-100 text-green-700' :
                          result.is_hallucination === 1 ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          { result.is_hallucination === 0 ? 'Passed' :
                            result.is_hallucination === 1 ? 'Failed' :
                            'Uncertain'}
                           
                        </span>
                      </td>
                      {/* <td className="p-3 text-center text-gray-700">{result.entropy?.toFixed(2)}</td> */}
                     
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LLMHallucinationTester;